import { useState } from "react";
import { useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BG_URL =
  "https://images.unsplash.com/photo-1549946463-0c1ba6fb541f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzaGlweWFyZCUyMG5pZ2h0fGVufDB8fHx8MTc3OTY3OTE5OXww&ixlib=rb-4.1.0&q=85";

const WAVE_PATH =
  "M0,64 C120,44 240,84 360,64 S600,44 720,64 S960,84 1080,64 S1320,44 1440,64 S1680,84 1800,64 S2040,44 2160,64 S2400,84 2520,64 S2760,44 2880,64 L2880,150 L0,150 Z";

// "Southern Ocean" letters slide in one by one, then keep a gentle wave bob
// travelling through the word.
function WaveTitle() {
  let i = 0;
  const letters = (word, cls) =>
    word.split("").map((ch, k) => {
      const idx = i++;
      return (
        <span
          key={`${cls}-${k}`}
          className={`wave-letter ${cls}`}
          style={{ animationDelay: `${idx * 0.055}s, ${0.9 + idx * 0.13}s` }}
        >
          {ch}
        </span>
      );
    });
  return (
    <h1
      className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white"
      data-testid="login-wave-title"
    >
      {letters("Southern", "")}
      <span className="inline-block w-2.5" />
      {letters("Ocean", "text-[#0088CC]")}
    </h1>
  );
}

function Waves() {
  return (
    <div className="sos-waves" aria-hidden>
      {[
        ["sos-wave-1", 0.12],
        ["sos-wave-2", 0.08],
        ["sos-wave-3", 0.05],
      ].map(([cls, op]) => (
        <svg
          key={cls}
          className={`sos-wave ${cls}`}
          viewBox="0 0 2880 150"
          preserveAspectRatio="none"
        >
          <path d={WAVE_PATH} fill="#0088CC" fillOpacity={op} />
        </svg>
      ))}
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const expired = params.get("expired") === "1";
  const nextPath = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={nextPath} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome aboard");
      navigate(nextPath);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#F6F7F8] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/70" aria-hidden />
      <div className="absolute inset-0 sos-grid-bg opacity-30" aria-hidden />
      <Waves />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="mb-8 flex flex-col items-center text-center gap-4">
          <Logo className="h-16 w-auto sos-fade-up" showWordmark={false} />
          <div>
            <WaveTitle />
            <p
              className="mt-2 text-xs uppercase tracking-[0.35em] text-[#5B6B7C] sos-fade-up"
              style={{ animationDelay: "0.8s" }}
            >
              Solutions
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="sos-surface rounded-lg p-6 space-y-5 sos-fade-up"
          data-testid="login-form"
        >
          {expired && (
            <div
              className="text-sm text-[#FBBF24] border border-[#FBBF24]/30 bg-[#FFF7E6]/60 px-3 py-2 rounded"
              data-testid="login-expired-notice"
            >
              Your session has expired. Please sign in again to continue.
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="login-password-input"
            />
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs text-[#0088CC] hover:underline font-medium"
                data-testid="login-forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          </div>
          {error && (
            <div
              className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-2 rounded"
              data-testid="login-error"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide"
            data-testid="login-submit-btn"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-center pt-2 border-t border-[#DDE5EC]">
            <p className="text-xs text-[#5B6B7C]">
              New here?{" "}
              <Link
                to="/register"
                className="text-[#0088CC] hover:underline font-medium"
                data-testid="login-register-link"
              >
                Create a client account
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
